import React, { useMemo, useRef, useState } from "react";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Tooltip } from "@mui/material";
import { RotateCcw, AlertTriangle, Clock, X, CheckCircle2, Copy, Rocket, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import toast from "react-hot-toast";
import PaginationFooter from "../common/PaginationFooter";
import StatusChip from "../common/StatusChip";
import { formatDisplayDate, formatDisplayDateTime } from "../../utils/helperFunctions";
import { displayIdOrOrphan } from "../../utils/constants/ads";
import { LAUNCH_STATUS_HINTS, SYNC_RETRY_STATES } from "../../utils/constants/AdLaunch";
import LaunchStatusChip from "./LaunchStatusChip";
import AdsetSyncRetry from "./AdsetSyncRetry";

const thSx = { py: 1.5, px: 2, fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", border: "none", backgroundColor: "#f9fafb" };
const tableSx = {
  fontFamily: "'DM Sans', system-ui, sans-serif",
  "& .MuiTableCell-root": { fontFamily: "'DM Sans', system-ui, sans-serif", verticalAlign: "top" },
  "& .MuiTableBody-root .MuiTableCell-root": { borderBottom: "1px solid #f3f4f6", py: 1.75, px: 2 },
  // Sticky cells need an opaque background, so rows set solid colours instead of MUI's translucent hover.
  "& .sticky-name-col": { position: "sticky", left: 0, zIndex: 1, backgroundColor: "inherit" },
  "& .MuiTableHead-root .sticky-name-col": { zIndex: 3, backgroundColor: "#f9fafb" },
  "& .name-col-resizer": { position: "absolute", top: 0, right: 0, width: "6px", height: "100%", cursor: "col-resize", userSelect: "none" },
  "& .name-col-resizer:hover": { backgroundColor: "#7f7ad5" },
};
const COLUMNS = ["Ad", "Agency", "Status", "Launch Date", "Meta", "When", ""];

// Ad column: drag its right edge to resize, like the ad request table; the width is kept per browser.
const AD_MIN_WIDTH = 220;
const AD_MAX_WIDTH = 720;
const AD_DEFAULT_WIDTH = 340;
const AD_WIDTH_KEY = "ams:adLaunchTable:adWidth";
const readStoredAdWidth = () => {
  try {
    const saved = Number(window.localStorage.getItem(AD_WIDTH_KEY));
    return Number.isFinite(saved) && saved > 0 ? Math.min(AD_MAX_WIDTH, Math.max(AD_MIN_WIDTH, saved)) : AD_DEFAULT_WIDTH;
  } catch {
    return AD_DEFAULT_WIDTH;
  }
};

// Rows with no launch record report "ready", but ads still in QC or moderation are not launchable.
const PRE_LAUNCH_STATES = new Set(["qc_pending", "qc_changes_requested", "moderation_pending", "moderation_approved", "moderation_rejected", "final_rejected"]);

const Muted = ({ children, className = "" }) => <p className={`truncate text-xs text-gray-500 ${className}`}>{children}</p>;

const ActionButton = ({ tone = "gray", icon: Icon, children, ...props }) => {
  const tones = {
    gray: "text-gray-700 bg-white border-gray-200 hover:bg-gray-50",
    green: "text-emerald-700 bg-white border-emerald-200 hover:bg-emerald-50",
    red: "text-rose-700 bg-white border-rose-200 hover:bg-rose-50",
    amber: "text-amber-700 bg-white border-amber-200 hover:bg-amber-50",
  };
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2 py-1 text-xs font-medium shadow-sm transition-colors disabled:opacity-50 ${tones[tone]}`}
      {...props}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </button>
  );
};

const SUMMARY_LABEL = { success: "live", scheduled: "scheduled", queued: "queued", processing: "processing", meta_scheduled: "launch scheduled", failed: "failed", schedule_failed: "schedule failed", cancelled: "cancelled", ready: "ready" };
const SUMMARY_HINT = Object.fromEntries(Object.entries(SUMMARY_LABEL).map(([status, label]) => [label, LAUNCH_STATUS_HINTS[status]]));
SUMMARY_HINT["in review"] = LAUNCH_STATUS_HINTS.in_review;
const SUMMARY_TONE = {
  live: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  "schedule failed": "bg-rose-50 text-rose-700 border-rose-200",
  "launch scheduled": "bg-blue-50 text-blue-700 border-blue-200",
  ready: "bg-sky-50 text-sky-700 border-sky-200",
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
};

const hookState = (r) => {
  const status = (r.status || "").toLowerCase();
  if (!r.launch_id && PRE_LAUNCH_STATES.has(r.pipeline_status)) return "in review";
  return SUMMARY_LABEL[status] || status || "in review";
};

/** Hooks in each ad set by state, in a fixed order. */
const summarize = (hooks) => {
  const counts = {};
  hooks.forEach((r) => {
    const s = hookState(r);
    counts[s] = (counts[s] || 0) + 1;
  });
  return ["live", "failed", "schedule failed", "launch scheduled", "scheduled", "queued", "processing", "ready", "in review", "cancelled"]
    .filter((s) => counts[s])
    .map((s) => ({ state: s, count: counts[s] }));
};

const groupRows = (rows) => {
  const out = [];
  rows.forEach((r) => {
    const key = r.group_key || `row:${r.ad_moderate_id}-${r.launch_id || 0}`;
    const last = out[out.length - 1];
    if (last && last.key === key) last.rows.push(r);
    else out.push({ key, rows: [r] });
  });
  return out;
};

const copy = (text, label) =>
  navigator.clipboard?.writeText(text).then(() => toast.success(`${label} copied`));

/** Launches grouped by ad set, one row per hook under each ad set header. */
export default function LaunchesTable({
  rows, isLoading, totalCount, page, setPage, pageSize, onPageSizeChange,
  canCreateLaunch, canSetLive, onOpenDetail, onOpenRequest,
  onRetry, retryPending, onCancelRow, cancelPending, onMarkLiveRow, setLivePending,
}) {
  const [adWidth, setAdWidth] = useState(readStoredAdWidth);
  const adWidthRef = useRef(adWidth);
  const startAdResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = adWidthRef.current;
    const onMove = (ev) => {
      const next = Math.min(AD_MAX_WIDTH, Math.max(AD_MIN_WIDTH, startWidth + ev.clientX - startX));
      adWidthRef.current = next;
      setAdWidth(next);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      try {
        window.localStorage.setItem(AD_WIDTH_KEY, String(adWidthRef.current));
      } catch {}
    };
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };
  const adCellSx = { width: adWidth, minWidth: adWidth, maxWidth: adWidth };
  const groups = useMemo(() => groupRows(rows), [rows]);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggleGroup = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const renderGroupHeader = (group) => {
    const first = group.rows[0];
    const isOpen = !collapsed.has(group.key);
    const total = first.hooks_count || group.rows.length;
    const accounts = [...new Set(group.rows.map((r) => r.meta_ad_account_name).filter(Boolean))];
    return (
      <TableRow
        key={`group-${group.key}`}
        onClick={() => toggleGroup(group.key)}
        sx={{
          cursor: "pointer",
          backgroundColor: "#f6f5fe",
          "&:hover": { backgroundColor: "#efedfc" },
          "& > td": {
            py: 2,
            borderTop: "1px solid #e4e1f7",
            borderBottom: isOpen ? "1px solid #e4e1f7" : "1px solid #e5e7eb",
          },
        }}
      >
        <TableCell className="sticky-name-col" sx={{ ...adCellSx, boxShadow: "inset 3px 0 0 #7f7ad5, inset -1px 0 0 #e5e7eb" }}>
          <div className="flex items-start gap-2">
            {isOpen ? (
              <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
            ) : (
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
            )}
            <div className="min-w-0">
              <Tooltip title={first.adset_name || first.topic || ""} arrow placement="bottom-start">
                <p className="truncate text-sm font-semibold text-gray-900">{first.adset_name || first.topic || "—"}</p>
              </Tooltip>
              <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-500">
                {first.topic && <span className="truncate">{first.topic}</span>}
                <span className="shrink-0">· Parent Id: {displayIdOrOrphan(first.ad_id)}</span>
                <span className="shrink-0">· {total} hook{total === 1 ? "" : "s"}</span>
                {first.ad_request_id && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onOpenRequest(first.ad_request_id); }}
                    className="shrink-0 text-gray-400 hover:text-[#5c55c2]"
                    title="Open ad request"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </button>
                )}
              </p>
            </div>
          </div>
        </TableCell>
        <TableCell sx={{ maxWidth: 170 }}>
          <p className="truncate text-sm font-medium text-gray-800" title={first.agency_name}>{first.agency_name || "—"}</p>
          <Muted>{first.account || ""}</Muted>
        </TableCell>
        <TableCell sx={{ maxWidth: 220 }}>
          <div className="flex flex-wrap items-center gap-1">
            {summarize(group.rows).map(({ state, count }) => (
              <Tooltip key={state} arrow title={SUMMARY_HINT[state] || ""}>
                <span
                  className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium ${SUMMARY_TONE[state] || "bg-gray-50 text-gray-600 border-gray-200"}`}
                >
                  {count} {state}
                </span>
              </Tooltip>
            ))}
            <span className="text-[11px] text-gray-400">of {total}</span>
          </div>
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <p className="text-sm text-gray-700">{formatDisplayDate(first.launch_date) ?? "—"}</p>
        </TableCell>
        <TableCell sx={{ maxWidth: 190 }}>
          <p className="truncate text-sm text-gray-600" title={accounts.join(", ")}>{accounts.join(", ") || "—"}</p>
        </TableCell>
        <TableCell />
        <TableCell>
          {canSetLive && first.ad_request_id && group.rows.some((r) => SYNC_RETRY_STATES.includes(hookState(r))) && (
            <AdsetSyncRetry adRequestId={first.ad_request_id} adsetName={first.adset_name} />
          )}
        </TableCell>
      </TableRow>
    );
  };

  const renderHookRow = (r, isLast) => {
                const status = (r.status || "").toLowerCase();
                const isFailed = status === "failed" || status === "schedule_failed";
                const isScheduled = status === "scheduled";
                const goLiveAt = status === "meta_scheduled" ? r.launch_settings?.start_time : null;
                const isCancelled = status === "cancelled";
                const prelaunchState = !r.launch_id && PRE_LAUNCH_STATES.has(r.pipeline_status) ? r.pipeline_status : null;
                const adName = r.hook_name || r.delivery_ad_name || "";
                const meta = [r.for_app_name, r.track, r.ad_moderate_id ? `Mod #${r.ad_moderate_id}` : null]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <TableRow
                    key={`${r.ad_moderate_id}-${r.launch_id || 0}`}
                    onClick={() => r.ad_request_id && onOpenRequest(r.ad_request_id)}
                    sx={{
                      cursor: r.ad_request_id ? "pointer" : "default",
                      backgroundColor: isFailed ? "#fff7f7" : "#fff",
                      "&:hover": { backgroundColor: isFailed ? "#ffefef" : "#f9fafb" },
                      "& > td": { py: 1.5, borderBottom: isLast ? "1px solid #e5e7eb" : "1px solid #f3f4f6" },
                    }}
                  >
                    <TableCell
                      className="sticky-name-col"
                      sx={{
                        ...adCellSx,
                        boxShadow: isFailed ? "inset 3px 0 0 #fb7185, inset -1px 0 0 #e5e7eb" : "inset -1px 0 0 #e5e7eb",
                      }}
                    >
                      <span className={`pointer-events-none absolute left-[28px] top-0 w-px bg-[#d9d6f5] ${isLast ? "h-[24px]" : "h-full"}`} />
                      <span className="pointer-events-none absolute left-[28px] top-[24px] h-px w-[12px] bg-[#d9d6f5]" />
                      <Tooltip
                        arrow
                        placement="bottom-start"
                        title={
                          <span>
                            {adName || "—"}
                            <br />
                            {meta}
                            {r.ad_id != null && <> · Parent {displayIdOrOrphan(r.ad_id)}</>}
                          </span>
                        }
                      >
                        <div className="min-w-0 pl-8">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {r.hook_number
                              ? [r.topic, `Hook ${r.hook_number}`].filter(Boolean).join(" · ")
                              : r.topic || r.adset_name || "—"}
                          </p>
                          <p className="truncate font-mono text-[11px] text-gray-500">{adName || "—"}</p>
                        </div>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 170 }} />
                    <TableCell sx={{ maxWidth: 220 }}>
                      <div className="flex flex-wrap items-center gap-1">
                        {isCancelled ? (
                          <LaunchStatusChip
                            status={r.status}
                            title={`Cancelled${r.cancelled_by_name ? ` by ${r.cancelled_by_name}` : ""}${r.cancelled_at ? ` at ${formatDisplayDateTime(r.cancelled_at)}` : ""}`}
                          />
                        ) : prelaunchState ? (
                          <Tooltip arrow title={LAUNCH_STATUS_HINTS[prelaunchState] || ""}>
                            <span><StatusChip subtle status={prelaunchState} /></span>
                          </Tooltip>
                        ) : (
                          <LaunchStatusChip status={r.status} />
                        )}
                        {r.auto_launch_date && (
                          <Tooltip arrow title={LAUNCH_STATUS_HINTS.auto_launch}>
                            <span><StatusChip subtle status="auto_launch" /></span>
                          </Tooltip>
                        )}
                        {r.meta_live_status && !prelaunchState && !isCancelled && (
                          <Tooltip arrow title={`Meta: ${r.meta_effective_status}${r.status_synced_at ? ` · checked ${formatDisplayDateTime(r.status_synced_at)}` : ""}`}>
                            <span><StatusChip subtle status={r.meta_live_status} /></span>
                          </Tooltip>
                        )}
                      </div>
                      {isFailed && r.error_message && (
                        <p className="mt-1 line-clamp-2 text-xs text-rose-600" title={r.error_message}>{r.error_message}</p>
                      )}
                    </TableCell>
                    <TableCell />
                    <TableCell sx={{ maxWidth: 190 }}>
                      {r.meta_ad_id ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); copy(r.meta_ad_id, "Meta ad ID"); }}
                          className="group inline-flex items-center gap-1 font-mono text-[11px] text-gray-500 hover:text-gray-800"
                          title="Copy Meta ad ID"
                        >
                          {r.meta_ad_id}
                          <Copy className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                        </button>
                      ) : (
                        <Muted>No Meta ad yet</Muted>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {isScheduled && r.scheduled_at ? (
                        <span className="inline-flex items-center gap-1 text-sm text-blue-700">
                          <Clock className="h-3.5 w-3.5" />
                          {formatDisplayDateTime(r.scheduled_at)}
                        </span>
                      ) : goLiveAt ? (
                        <Tooltip arrow title="Created on Meta; starts delivering at this time">
                          <span className="inline-flex items-center gap-1 text-sm text-blue-700">
                            <Clock className="h-3.5 w-3.5" />
                            {formatDisplayDateTime(goLiveAt)}
                          </span>
                        </Tooltip>
                      ) : (
                        <p className="text-sm text-gray-700">{r.launched_at ? formatDisplayDateTime(r.launched_at) : "—"}</p>
                      )}
                      {r.launched_by_name && <Muted>by {r.launched_by_name}</Muted>}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {canSetLive && status === "ready" && (
                          <Tooltip arrow title="Mark as Live (for ads launched outside the platform)">
                            <span>
                              <ActionButton tone="green" icon={CheckCircle2} onClick={() => onMarkLiveRow(r)} disabled={setLivePending}>
                                Mark live
                              </ActionButton>
                            </span>
                          </Tooltip>
                        )}
                        {isScheduled && canCreateLaunch && r.launch_id && (
                          <ActionButton tone="red" icon={X} onClick={() => onCancelRow(r)} disabled={cancelPending}>
                            Cancel
                          </ActionButton>
                        )}
                        {isFailed && (
                          <>
                            <ActionButton tone="amber" icon={AlertTriangle} onClick={() => r.launch_id && onOpenDetail(r.launch_id)}>
                              Details
                            </ActionButton>
                            {canCreateLaunch && r.launch_id && (
                              <ActionButton icon={RotateCcw} onClick={() => onRetry(r.launch_id)} disabled={retryPending}>
                                Retry{r.retry_count ? ` (${r.retry_count})` : ""}
                              </ActionButton>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
  };

  return (
    <Box sx={{ flex: "1 1 auto", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <TableContainer sx={{ flex: "1 1 auto", minHeight: 0 }}>
        <Table size="small" sx={tableSx} stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) =>
                c === "Ad" ? (
                  <TableCell key="ad" className="sticky-name-col" sx={{ ...thSx, ...adCellSx, boxShadow: "inset -1px 0 0 #e5e7eb" }}>
                    Ad
                    <div
                      className="name-col-resizer"
                      role="separator"
                      aria-orientation="vertical"
                      aria-label="Resize the ad column"
                      onMouseDown={startAdResize}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                ) : (
                  <TableCell key={c || "actions"} sx={thSx} align={c ? "left" : "right"}>{c}</TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 8 }}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
                    <CircularProgress size={20} sx={{ color: "#7f7ad5" }} />
                    <span className="text-sm font-medium text-[#7f7ad5]">Loading launches…</span>
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length > 0 ? (
              groups.map((group, gi) => (
                <React.Fragment key={group.key}>
                  {gi > 0 && (
                    <TableRow aria-hidden="true">
                      <TableCell colSpan={COLUMNS.length} sx={{ p: "0 !important", height: 16, backgroundColor: "#f3f4f6", borderBottom: "none !important" }} />
                    </TableRow>
                  )}
                  {renderGroupHeader(group)}
                  {!collapsed.has(group.key) &&
                    group.rows.map((r, i) => renderHookRow(r, i === group.rows.length - 1))}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 10, borderBottom: "none" }}>
                  <div className="flex flex-col items-center gap-2 text-gray-500">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                      <Rocket className="h-5 w-5 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">No launches match these filters</p>
                    <p className="text-xs">Final-approved hooks from Ad Moderation show up here, ready to launch.</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PaginationFooter shown={groups.length} totalCount={totalCount} page={page} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={onPageSizeChange} />
    </Box>
  );
}
